/**
 * Visit Notes Service Implementation (Core Layer)
 *
 * Business logic for visit notes management
 */

import type {
  IVisitNotesService,
  IVisitNotesRepository,
  ICreateVisitNoteRequest,
  IUpdateVisitNoteRequest,
  ICreateVisitNoteResponse,
  IUpdateVisitNoteResponse,
  IGetAllVisitNotesResponse,
  IGetVisitNoteProvidersResponse,
  ISummarizeNarrativeRequest,
  ISummarizeNarrativeResponse,
  ISendEfaxRequest,
  ISendEfaxResponse,
  IGetSidePeekProfileResponse,
  IGenerateSidePeekProfileResponse,
  IUpdateSidePeekProfileResponse,
  IGetSafetyNoteResponse,
  IUpdateSafetyNoteResponse,
  IGetCodeStatusResponse,
  IUpdateCodeStatusResponse,
  CodeStatusValue,
  IGetPatientContactsResponse,
  IUpdatePatientContactsResponse,
  IUpdatePatientContactsRequest,
  IGetNextVisitPlanResponse,
  IUpdateNextVisitPlanResponse,
  INextVisitPlan,
  IGetPatientSummaryResponse,
  IGeneratePatientSummaryResponse,
  IGetNoteLlmOutputResponse,
  IClearManualEditsHistoryResponse,
} from "../ports/driver/VisitNotesService";

export const VisitNotesServiceImplFactory = ({
  visitNotesRepository,
}: {
  visitNotesRepository: IVisitNotesRepository;
}): IVisitNotesService => {
  /**
   * Create a new visit note
   */
  const createVisitNote = async (
    request: ICreateVisitNoteRequest
  ): Promise<ICreateVisitNoteResponse> => {
    try {
      const data = await visitNotesRepository.createVisitNoteAPI(request);

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to create visit note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to create visit note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update an existing visit note
   */
  const updateVisitNote = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateVisitNoteRequest
  ): Promise<IUpdateVisitNoteResponse> => {
    try {
      const data = await visitNotesRepository.updateVisitNoteAPI(
        clinicId,
        patientId,
        noteId,
        request
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update visit note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update visit note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get all visit notes for a clinic
   */
  const getAllVisitNotes = async (
    clinicId: number,
    params?: {
      monthOffset?: number;
      daysBack?: number;
      daysForward?: number;
      providerEmail?: string;
      providerName?: string;
      includeEmpty?: boolean;
      skipHealthscribe?: boolean;
    }
  ): Promise<IGetAllVisitNotesResponse> => {
    try {
      const data = await visitNotesRepository.getAllVisitNotesAPI(
        clinicId,
        params
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get all visit notes:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get all visit notes",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get distinct providers for the visit notes provider filter dropdown
   */
  const getVisitNoteProviders = async (
    clinicId: number
  ): Promise<IGetVisitNoteProvidersResponse> => {
    try {
      const data = await visitNotesRepository.getVisitNoteProvidersAPI(
        clinicId
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get visit note providers:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get visit note providers",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Server-side search for visit notes (transcript search only)
   */
  const searchVisitNotes = async (
    clinicId: number,
    searchQuery?: string,
    limit?: number
  ): Promise<IGetAllVisitNotesResponse> => {
    try {
      const data = await visitNotesRepository.searchVisitNotesAPI(
        clinicId,
        searchQuery,
        limit
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to search visit notes:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to search visit notes",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get archived visit notes for a clinic
   */
  const getArchivedVisitNotes = async (
    clinicId: number
  ): Promise<IGetAllVisitNotesResponse> => {
    try {
      const data = await visitNotesRepository.getArchivedVisitNotesAPI(
        clinicId
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get archived visit notes:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get archived visit notes",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Restore an archived visit note
   */
  const restoreArchivedVisitNote = async (
    clinicId: number,
    ipvNoteId: number
  ): Promise<{ success: boolean; error?: { message: string } }> => {
    try {
      await visitNotesRepository.restoreArchivedVisitNoteAPI(
        clinicId,
        ipvNoteId
      );
      return { success: true };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to restore archived visit note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to restore note",
        },
      };
    }
  };

  /**
   * Get a single visit note
   */
  const getVisitNote = async (
    noteId: number
  ): Promise<{ success: boolean; data?: any; error?: any }> => {
    try {
      const data = await visitNotesRepository.getVisitNoteAPI(noteId);

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to get visit note:", error);
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get visit note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Summarize a narrative for sharing
   */
  const summarizeNarrative = async (
    request: ISummarizeNarrativeRequest
  ): Promise<ISummarizeNarrativeResponse> => {
    try {
      const response = await visitNotesRepository.summarizeNarrativeAPI(
        request
      );

      return response;
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to summarize narrative:",
        error
      );
      return {
        success: false,
        error: error?.message || "Failed to summarize narrative",
      };
    }
  };

  /**
   * Send visit note PDF via eFax
   */
  const sendEfax = async (
    request: ISendEfaxRequest
  ): Promise<ISendEfaxResponse> => {
    try {
      const response = await visitNotesRepository.sendEfaxAPI(request);
      return response;
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to send eFax:", error);
      throw error;
    }
  };

  /**
   * Get Side Peek Profile for a patient
   */
  const getSidePeekProfile = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSidePeekProfileResponse> => {
    try {
      const result = await visitNotesRepository.getSidePeekProfileAPI(
        clinicId,
        patientId
      );

      const dobFromInfo =
        result.patientInfo?.dob != null &&
        String(result.patientInfo.dob).trim() !== ""
          ? String(result.patientInfo.dob).trim()
          : undefined;

      return {
        success: true,
        data: result.sidepeek_profile || undefined,
        patient_dob: result.patient_dob ?? dobFromInfo,
        patientInfo: result.patientInfo,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get side peek profile:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get side peek profile",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Trigger Side Peek Profile generation for a patient
   */
  const generateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    section?: "diagnoses" | "medications" | "allergies",
    model?: string
  ): Promise<IGenerateSidePeekProfileResponse> => {
    try {
      const data = await visitNotesRepository.generateSidePeekProfileAPI(
        clinicId,
        patientId,
        noteId,
        section,
        model
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to generate side peek profile:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to generate side peek profile",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Side Peek Profile for a patient (manual edits)
   */
  const updateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    updateData: {
      diagnoses?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        is_primary?: boolean | null;
        onset_date?: string | null;
      }>;
      medications?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        status?: string | null;
      }>;
      allergies?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        status?: string | null;
        allergen?: string | null;
        reaction?: string | null;
        severity?: string | null;
        onset_date?: string | null;
        is_primary?: boolean | null;
      }>;
    }
  ): Promise<IUpdateSidePeekProfileResponse> => {
    try {
      const data = await visitNotesRepository.updateSidePeekProfileAPI(
        clinicId,
        patientId,
        updateData
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update side peek profile:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update side peek profile",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Safety Note for a patient
   */
  const getSafetyNote = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSafetyNoteResponse> => {
    try {
      const data = await visitNotesRepository.getSafetyNoteAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to get safety note:", error);
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get safety note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Safety Note for a patient
   */
  const updateSafetyNote = async (
    clinicId: number,
    patientId: number,
    content: string
  ): Promise<IUpdateSafetyNoteResponse> => {
    try {
      const data = await visitNotesRepository.updateSafetyNoteAPI(
        clinicId,
        patientId,
        content
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update safety note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update safety note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Code Status for a patient
   */
  const getCodeStatus = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetCodeStatusResponse> => {
    try {
      const data = await visitNotesRepository.getCodeStatusAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to get code status:", error);
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get code status",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Code Status for a patient
   */
  const updateCodeStatus = async (
    clinicId: number,
    patientId: number,
    status: CodeStatusValue,
    otherStatus?: string | null
  ): Promise<IUpdateCodeStatusResponse> => {
    try {
      const data = await visitNotesRepository.updateCodeStatusAPI(
        clinicId,
        patientId,
        status,
        otherStatus
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update code status:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update code status",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Patient Contacts for a patient
   */
  const getPatientContacts = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientContactsResponse> => {
    try {
      const result = await visitNotesRepository.getPatientContactsAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data: result.contacts,
        patientInfo: result.patientInfo,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get patient contacts:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get patient contacts",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Add or remove a patient contact
   */
  const updatePatientContacts = async (
    clinicId: number,
    patientId: number,
    request: IUpdatePatientContactsRequest
  ): Promise<IUpdatePatientContactsResponse> => {
    try {
      const data = await visitNotesRepository.updatePatientContactsAPI(
        clinicId,
        patientId,
        request
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update patient contacts:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update patient contacts",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Next Visit Plan for a patient
   */
  const getNextVisitPlan = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetNextVisitPlanResponse> => {
    try {
      const data = await visitNotesRepository.getNextVisitPlanAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        ...(data !== null ? { data } : {}),
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to get next visit plan:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get next visit plan",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Next Visit Plan for a patient
   */
  const updateNextVisitPlan = async (
    clinicId: number,
    patientId: number,
    plan: INextVisitPlan["plan"]
  ): Promise<IUpdateNextVisitPlanResponse> => {
    try {
      const data = await visitNotesRepository.updateNextVisitPlanAPI(
        clinicId,
        patientId,
        plan
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to update next visit plan:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update next visit plan",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get the stored patient summary.
   * If the summary does not exist yet (exists: false), the caller should
   * trigger generatePatientSummary to create one on demand.
   */
  const getPatientSummary = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientSummaryResponse> => {
    try {
      const data = await visitNotesRepository.getPatientSummaryAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to get patient summary:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get patient summary",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Trigger on-demand generation of the patient summary via the POST endpoint.
   */
  const generatePatientSummary = async (
    clinicId: number,
    patientId: number
  ): Promise<IGeneratePatientSummaryResponse> => {
    try {
      const data = await visitNotesRepository.generatePatientSummaryAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to generate patient summary:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to generate patient summary",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get latest LLM output for a note
   */
  const getNoteLlmOutput = async (
    noteId: number,
    clinicId: number
  ): Promise<IGetNoteLlmOutputResponse> => {
    try {
      const data = await visitNotesRepository.getNoteLlmOutputAPI(
        noteId,
        clinicId
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to get note LLM output:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get LLM output",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Clear manual edits history for a note
   */
  const clearManualEditsHistory = async (
    noteId: number,
    clinicId: number
  ): Promise<IClearManualEditsHistoryResponse> => {
    try {
      await visitNotesRepository.clearManualEditsHistoryAPI(noteId, clinicId);
      return { success: true };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to clear manual edits history:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to clear manual edits history",
          code: error?.code,
        },
      };
    }
  };

  return {
    createVisitNote,
    updateVisitNote,
    getAllVisitNotes,
    getVisitNoteProviders,
    searchVisitNotes,
    getArchivedVisitNotes,
    restoreArchivedVisitNote,
    getVisitNote,
    summarizeNarrative,
    sendEfax,
    getSidePeekProfile,
    generateSidePeekProfile,
    updateSidePeekProfile,
    getSafetyNote,
    updateSafetyNote,
    getCodeStatus,
    updateCodeStatus,
    getPatientContacts,
    updatePatientContacts,
    getNextVisitPlan,
    updateNextVisitPlan,
    getPatientSummary,
    generatePatientSummary,
    getNoteLlmOutput,
    clearManualEditsHistory,
  };
};
